import { Subject, Observable, merge, fromEvent, of } from 'rxjs';
import { takeUntil, first, switchMap, tap, take } from 'rxjs/operators';

import {
  Component,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  ElementRef,
  Inject,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnUser } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';
import { SnCollection } from '@lib/common/models';
import { SnLiveUser } from '@lib/live-tracking/models';
import { t } from '@lib/translation/helpers';
import { SnActionConfig } from 'libs/ui/actions/src/lib/types';
import { DragBarComponent } from 'libs/ui/drag-bar/src/lib/drag-bar/drag-bar.component';

import { SnChannel, SnChatUser } from '../../models';
import { SnChannelsService, SnChatService, SnMembersService } from '../../services';

@Component({
  selector: 'sn-chat-drawer',
  templateUrl: './chat-drawer.component.html',
  styleUrls: ['./chat-drawer.component.scss'],
  providers: [DragBarComponent],
})
export class SnChatDrawerComponent implements OnDestroy, AfterViewInit {
  @ViewChild(DragBarComponent)
  public dragBar: DragBarComponent;

  public channels: SnChannel[];
  public user: SnUser;
  public userHasAccess: boolean = false;
  public form = new UntypedFormGroup({
    channel_id: new UntypedFormControl(''),
  });
  public minMaxBtns: SnActionConfig[] = this._genActions();
  public extraActionBtns: SnActionConfig[] = this._extraActions();

  public channel: SnChannel;
  public chatLoaded$ = new Observable<boolean>();
  public isMinimized: boolean = false;
  public actionButtonHidden: boolean;
  public isLoading: boolean = false;
  public header: string = t('map.tools.chatDrawer.fususChatPopout');
  private _destroy$ = new Subject();
  private _channelChanged$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      users: SnLiveUser[];
      channelType: string;
      channel: SnChannel;
      isMinimized?: boolean;
    },
    private readonly _chatService: SnChatService,
    private readonly _channelsService: SnChannelsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _elementRef: ElementRef,
    private readonly _matDialogRef: MatDialogRef<SnChatDrawerComponent>,
    private readonly _membersService: SnMembersService,
    private readonly _currentUser: SnCurrentUser,
  ) {
    this.chatLoaded$ = this._chatService.chatIsReady$;
    this.user = this._currentUser;
    merge(this._chatService.channelsLoaded$, this._chatService.channelsUpdated$)
      .pipe(first(), takeUntil(this._destroy$))
      .subscribe(() => {
        this.channels = this._chatService.channels;
      });
    if (this._chatService.channels) {
      this.channels = this._chatService.channels;
    }

    this._chatService.activeChannelChanged$.pipe(takeUntil(this._destroy$)).subscribe((channel) => {
      if (!channel) {
        return;
      }
      this.channel = channel;
      this._checkUserAccess(channel);
      this._changeDetectorRef.detectChanges();
    });
  }

  get activeChannel(): SnChannel {
    return this.channel;
  }

  public ngAfterViewInit(): void {
    this._elementRef.nativeElement.parentElement.parentElement.style['pointer-events'] = 'none';
    if (this.data.users?.length > 0) {
      const usersIds: string[] = this.data.users.map((user) => user.id);
        this._createChannel(usersIds);
    }
  }

  public ngOnDestroy(): void {
    this._chatService.resetActiveChannel();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public selectChannel(channel: SnChannel): void {
    this.channel = channel;
      this._checkUserAccess(channel);
    this._channelChanged$.next();
    this.dragBar.updateDisabledBtns(true);
    this._initEvent();
    this._changeDetectorRef.detectChanges();
    setTimeout(() => {
      this._scrollToBottom();
    }, 1000);
    this._changeDetectorRef.detectChanges();
  }

  public unselectChannel(): void {
    this.channel = null;
    this.userHasAccess = false;
    this.dragBar.updateDisabledBtns(false);
    this._chatService.resetActiveChannel();
    this._changeDetectorRef.detectChanges();
  }

  public toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    const cdkContainer: any = document.querySelector('.map__chat-drawer');
    if (!this.isMinimized) {
      this._matDialogRef.updateSize('580px', '700px');
      cdkContainer?.classList.add('sn-chat-drawer-minimized');
    } else {
      this.unselectChannel();
      this._matDialogRef.updateSize('580px', '6.5vh');
      cdkContainer?.classList.remove('sn-chat-drawer-minimized');
    }
    this.dragBar.drag.reset();
    this._changeDetectorRef.detectChanges();
    this.minMaxBtns = this._genActions();
  }

  public toggleMaximize(): void {
    this.isMinimized = !this.isMinimized;
    if (!this.isMinimized) {
      const cdkContainer: any = document.querySelector('.map__chat-drawer');
      this._matDialogRef.updateSize('580px', '700px');
      cdkContainer?.classList.remove('sn-chat-drawer-minimized');
    }
    this.dragBar.drag.reset();
    this._changeDetectorRef.detectChanges();
    this.minMaxBtns = this._genActions();
  }

  public join(): void {
    if (!this.activeChannel) {
      return;
    }

    this._membersService
      .joinToChannel(this.activeChannel.sid, this.activeChannel.type)
      .pipe(
        switchMap((c) => {
          return this._chatService.getTwilioChannel(c.sid).pipe(
            switchMap((tc) => {
              c.twilioChannel = tc;
              return of(c);
            }),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((channel) => {
        this.channel.users = channel.users;
        this.channel.twilioChannel = channel.twilioChannel;
        this.selectChannel(this.channel);
      });
  }

  public close(): void {
    this._matDialogRef.close();
  }

  public popout(): void {
    if (this.activeChannel?.twilioChannel) {
      window.open(`/chat/${this.activeChannel.twilioChannel.sid}`);
    }
  }

  private _initEvent(): void {
    if (!this.activeChannel?.twilioChannel) {
      return;
    }

    fromEvent(this.activeChannel.twilioChannel, 'updated')
      .pipe(takeUntil(this._channelChanged$), takeUntil(this._destroy$))
      .subscribe(() => {
        this._scrollToBottom();
      });
  }

  private _scrollToBottom(): void {
    const div = this._elementRef.nativeElement.querySelector('mat-dialog-content');
    div.scrollTop = div.scrollHeight;
  }

  private _createChannel(channelData: any): void {
    if (this.data.channelType === 'public') {
      this.selectChannel(this.data.channel);
    } else {
      this.isLoading = true;
      this._channelsService
        .createPairChannel(channelData[0])
        .pipe(
          switchMap((c) => {
            return this._chatService.getTwilioChannel(c.sid).pipe(
              switchMap((tc) => {
                c.twilioChannel = tc;
                if (this._chatService.channels.has(c.id)) {
                  this._chatService.setActiveChannel(c.sid);
                  this._changeDetectorRef.detectChanges();
                  return of(c);
                }
                return this._setActiveChannelAfterUpdate(c);
              }),
            );
          }),
          takeUntil(this._destroy$),
        )
        .subscribe({
          next: (channel) => {
            this.isLoading = false;
            this.selectChannel(channel);
            this._changeDetectorRef.detectChanges();
          },
          error: () => {
            this.isLoading = false;
            this._changeDetectorRef.detectChanges();
          },
        });
    }
  }

  private _setActiveChannelAfterUpdate(channel: SnChannel): Observable<SnChannel> {
    return this._chatService.channelsUpdated$.pipe(
      take(1),
      tap(() => {
        this._chatService.setActiveChannel(channel.sid);
        this._changeDetectorRef.detectChanges();
      }),
      switchMap(() => of(channel)),
    );
  }

  private _genActions(): SnActionConfig[] {
    return [
      {
        label: t('common.minimize'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-minimize',
        },
        click: () => {
          this.toggleMinimize();
        },
        hidden: () => this.isMinimized,
      },
      {
        label: t('common.maximize'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-maximize',
        },
        click: () => {
          this.toggleMinimize();
        },
        hidden: () => !this.isMinimized,
      },
    ];
  }

  private _extraActions(): SnActionConfig[] {
    return [
      {
        label: t('map.tools.chatDrawer.backToChatList'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-arrow-left',
        },
        click: () => {
          this.unselectChannel();
        },
      },
      {
        label: t('map.tools.chatDrawer.navigateToChatPage'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-up-right-from-square',
        },
        click: () => {
          this.popout();
        },
      },
    ];
  }

  private _checkUserAccess(channel: SnChannel): void {
    this._membersService
      .fetchMemberList(channel.sid, { pageSize: 'all' })
      .pipe(takeUntil(this._destroy$))
      .subscribe((value) => {
        channel.members = new SnCollection<SnChatUser>(...value.results);
        this.userHasAccess = channel.members.some((user) => this._currentUser.id === user.id);
      });
  }
}
